import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { SegmentService } from '../segment/segment.service';
import { ScrollDepthService } from '../scroll-depth/scroll-depth.service';
import type {
  ElementInteractedParams,
  ElementPosition,
  ElementTrackingConfig,
  ElementValue,
} from './element-tracking.types';

/**
 * ElementTrackingService (Client)
 *
 * Main orchestrator for tracking user interactions with UI elements via Segment.
 * Implements the 'Element Interacted' event from EPS-3234.
 *
 * Features:
 * - Auto-collects: location_url, scroll_depth_percent, interaction_source
 * - Integrates with SegmentService for event tracking
 * - Provides convenience methods for common interactions
 * - SSR-safe (gracefully no-ops during server-side rendering)
 *
 * @example
 * constructor(private elementTracking: ElementTrackingService) {}
 *
 * // Track form submission
 * this.elementTracking.trackFormSubmit(
 *   'home_emailform_signup_footer',
 *   'email_form',
 *   'footer',
 *   { email: 'user@example.com', subscription_list_id: '12345' },
 *   true
 * );
 *
 * // Track form focus
 * this.elementTracking.trackFormFocus(
 *   'home_emailform_signup_footer',
 *   'email_form',
 *   'footer'
 * );
 */
@Injectable({
  providedIn: 'root',
})
export class ElementTrackingService {
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private scrollDepthService: ScrollDepthService,
    private segmentService: SegmentService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Track a generic element interaction
   * Auto-collects: location_url, scroll_depth_percent, interaction_source
   *
   * @param config - Element tracking configuration
   * @param success - Whether the interaction was successful (default: true)
   * @param errors - Optional error information
   */
  trackInteraction(config: ElementTrackingConfig, success = true, errors?: ElementInteractedParams['error']): void {
    if (!this.isBrowser) {
      return; // Gracefully no-op during SSR
    }

    const params = this.buildParams(config, success, errors);
    void this.segmentService.callAnalyticsMethod('track', 'Element Interacted', params);
  }

  /**
   * Track a form field focus event
   * Convenience method for when a user focuses on a form field
   *
   * @param elementId - Element identifier (format: [page]_[component]_[descriptor]_[position])
   * @param elementType - Type of UI element (e.g., 'email_form')
   * @param elementPosition - Location within the page
   * @param elementValue - Optional element-specific data
   */
  trackFormFocus(
    elementId: string,
    elementType: string,
    elementPosition: ElementPosition,
    elementValue?: ElementValue,
  ): void {
    this.trackInteraction({
      elementId,
      elementType,
      elementPosition,
      interactionType: 'focus',
      interactionSource: 'user',
      elementState: 'focused',
      elementValue,
    });
  }

  /**
   * Track a form submission event
   * Convenience method for successful or failed form submissions
   *
   * @param elementId - Element identifier
   * @param elementType - Type of UI element
   * @param elementPosition - Location within the page
   * @param elementValue - Element-specific data (e.g., { email, subscription_list_id })
   * @param success - Whether the submission was successful
   * @param errors - Optional error information
   */
  trackFormSubmit(
    elementId: string,
    elementType: string,
    elementPosition: ElementPosition,
    elementValue: ElementValue,
    success = true,
    errors?: ElementInteractedParams['error'],
  ): void {
    this.trackInteraction(
      {
        elementId,
        elementType,
        elementPosition,
        interactionType: 'submit',
        interactionSource: 'user',
        elementState: 'submitted',
        elementValue,
      },
      success,
      errors,
    );
  }

  /**
   * Track a form error/validation failure
   * Convenience method for form validation errors
   *
   * @param elementId - Element identifier
   * @param elementType - Type of UI element
   * @param elementPosition - Location within the page
   * @param errorCode - Static error code or HTTP status code
   * @param errorMessage - Dynamic error message
   * @param elementValue - Optional element-specific data
   */
  trackFormError(
    elementId: string,
    elementType: string,
    elementPosition: ElementPosition,
    errorCode: string | number,
    errorMessage: string,
    elementValue?: ElementValue,
  ): void {
    this.trackInteraction(
      {
        elementId,
        elementType,
        elementPosition,
        interactionType: 'submit',
        interactionSource: 'user',
        elementState: 'abandoned',
        elementValue,
      },
      false,
      [{ error_code: errorCode, error_message: errorMessage }],
    );
  }

  /**
   * Track a click interaction
   * Convenience method for clicks on buttons, links, etc.
   *
   * @param elementId - Element identifier
   * @param elementType - Type of UI element
   * @param elementPosition - Location within the page
   * @param destinationUrl - Optional destination URL
   * @param elementValue - Optional element-specific data
   */
  trackClick(
    elementId: string,
    elementType: string,
    elementPosition: ElementPosition,
    destinationUrl?: string,
    elementValue?: ElementValue,
  ): void {
    this.trackInteraction({
      elementId,
      elementType,
      elementPosition,
      interactionType: 'click',
      interactionSource: 'user',
      destinationUrl,
      elementValue,
    });
  }

  /**
   * Build complete ElementInteractedParams from config
   * Auto-collects required properties
   */
  private buildParams(
    config: ElementTrackingConfig,
    success: boolean,
    errors?: ElementInteractedParams['error'],
  ): ElementInteractedParams {
    return {
      location_url: this.getLocationUrl(),
      destination_url: config.destinationUrl,
      interaction_source: config.interactionSource || 'user',
      interaction_type: config.interactionType,
      scroll_depth_percent: this.scrollDepthService.getCurrentScrollDepth(),
      element_id: config.elementId,
      element_value: config.elementValue,
      element_position: config.elementPosition,
      element_type: config.elementType,
      element_state: config.elementState,
      success,
      error: errors,
    };
  }

  /**
   * Get the current location URL with query params and hash
   * SSR-safe implementation using Angular Router and DOCUMENT
   */
  private getLocationUrl(): string {
    if (!this.isBrowser) {
      // During SSR, return the current route path
      return this.router.url;
    }

    // In browser, get full URL with query params and hash
    const location = this.document.defaultView?.location;
    if (location) {
      return location.href;
    }

    // Fallback to router URL
    return this.router.url;
  }

  /**
   * Auto-detect element position from DOM element
   * Checks if element is within header, footer, sidebar, or body
   *
   * @param element - DOM element to check
   * @returns Detected ElementPosition or null if cannot determine
   */
  detectElementPosition(element: HTMLElement): ElementPosition | null {
    if (!this.isBrowser) {
      return null;
    }

    let current: HTMLElement | null = element;

    while (current && current !== this.document.body) {
      const tagName = current.tagName.toLowerCase();
      const role = current.getAttribute('role');

      // Check for header
      if (tagName === 'header' || role === 'banner') {
        return 'header';
      }

      // Check for footer
      if (tagName === 'footer' || role === 'contentinfo') {
        return 'footer';
      }

      // Check for sidebar/aside
      if (tagName === 'aside' || role === 'complementary') {
        return 'sidebar';
      }

      current = current.parentElement;
    }

    // Default to body if no specific container found
    return 'body';
  }
}
